import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Branch } from '@/client/branches';
import {
  AccountsEnrollFormValues,
  Course,
  CourseAvailabilityFormValues,
  CourseAvailabilityTypeEnum,
  CourseEnrollmentTypeEnum,
  CourseEntityEnrollEnum,
  CourseEntityScheduleEnum,
  CourseStatus,
  DeleteEnrollmentFormValues,
  EnrollFormValues,
  MaterialType,
} from '@/client/courses';
import { Group } from '@/client/groups';
import { HubspotProperty, HubspotValue } from '@/client/hubspot/types';
import { Actions, Subjects, SystemRoles, User } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { LanguageContext } from '@/common/context/LanguageContext';
import { TranslationFunctionType } from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import { CourseTranslationStatusBanner } from '@/components/courses/CourseTranslationStatusBanner';
import { CourseAccountsEnrollModal } from '@/components/courses/modals/AccountsEnrollModal';
import { CourseAvailabilityModal } from '@/components/courses/modals/CourseAvailabilityModal';
import { CourseMigrationModal } from '@/components/courses/modals/CourseMigrationModal';
import { EnrollModal } from '@/components/courses/modals/EnrollModal';
import {
  ViewOnlyRestriction,
  ViewOnlyRestrictionWhen,
} from '@/components/custom-roles';
import { DataTableActions } from '@/components/tables/crud';
import {
  useAccountsSchedules,
  useBranchesSchedules,
  useDeleteCourse,
  useDeleteEnrollment,
  useGroupsSchedules,
  useMakeAccountsAvailable,
  useMakeAccountsUnavailable,
  useUpdateCourse,
  useUsersSchedules,
} from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { hasRestrictedUserScope } from '@/utils/custom-roles';
import {
  branchAdminCheck,
  getTranslationByLanguage,
  isDemoRestrictedMode,
  materialByTypeListPath,
  WhiteMagicPencilIcon,
} from '@/utils/helpers';
import { hubspotTrack } from '@/utils/hubspot';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import React, { SyntheticEvent, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';

const getBreadcrumbs = (
  account: Account,
  course: Course,
  language: LanguagesEnum,
  location: Location,
  t: TranslationFunctionType,
  returnUrl?: string,
  onRequestLeave?: (url: string) => void,
): MenuItem[] => {
  const isResource = course.type === MaterialType.RESOURCE;
  const isPoster = course.type === MaterialType.POSTER;
  const isCustomMaterial = course.type === MaterialType.CUSTOM_MATERIAL;
  const isMaterial = isResource || isCustomMaterial || isPoster;

  const items: MenuItem[] = [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  if (isMaterial && !isCustomMaterial) {
    items.push({
      label: isResource ? t('templates') : t('posters'),
      url: materialByTypeListPath(course.type),
      className: 'active',
      template: AppBreadCrumbTemplate,
    });
  } else {
    const contentUrl = returnUrl || RedirectPaths[RedirectPathsEnum.COURSES]();

    items.push({
      label: t('content'),
      url: contentUrl,
      command: onRequestLeave ? () => onRequestLeave(contentUrl) : undefined,
      template: AppBreadCrumbTemplate,
    });
  }

  items.push({
    label: getTranslationByLanguage(
      course?.courseTranslations,
      language as LanguagesEnum,
      course.defaultLanguage,
    )?.name,
    url: `${location?.pathname}${location?.search}`,
    template: AppBreadCrumbTemplate,
  });

  return items;
};

type CoursesTabsProps = {
  course: Course;
  selfHosted?: boolean;
  onChangeCourseType?: (selfHosted: boolean) => void;
  onTriggerRefetch?: () => void;
  returnUrl?: string;
  hasBlockEmailsBanner?: boolean;
  onRequestLeave?: (url: string) => void;
};

export const CoursesActionHeader: React.FC<CoursesTabsProps> = ({
  course,
  selfHosted,
  onChangeCourseType,
  onTriggerRefetch,
  returnUrl,
  hasBlockEmailsBanner,
  onRequestLeave,
}) => {
  const { t } = useTranslation();
  const { can, isRestrictedByRole } = usePermission();
  const isCustomizeRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.COURSES,
  );
  const isSchedulingRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.COURSE_USER_SCHEDULES,
  );
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const { user } = useAppSelector(selectCurrentUserState);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const { setDialogData } = useContext(DialogContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);

  const retired = course.status === CourseStatus.RETIRED;
  const isResource = course.type === MaterialType.RESOURCE;
  const isCustomMaterial = course.type === MaterialType.CUSTOM_MATERIAL;
  const isPoster = course.type === MaterialType.POSTER;
  const isMaterial = isResource || isCustomMaterial || isPoster;
  const isBranchAdmin = branchAdminCheck(user, account);
  const isScopeRestricted = hasRestrictedUserScope(user?.customRole?.scopeMode);
  const isCustomCourse = course.account;

  const saveAvailableAccounts = useMakeAccountsAvailable();
  const saveUnavailableAccounts = useMakeAccountsUnavailable();

  const [showMakeAvailableModal, setShowMakeAvailableModal] = useState(false);
  const [showMakeUnavailableModal, setShowMakeUnavailableModal] =
    useState(false);
  const [showCourseAccountsEnrollModal, setCourseAccountsEnrollModal] =
    useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);

  const [showEnrollModal, setEnrollModal] = useState(false);
  const [showUnenrollModal, setUnenrollModal] = useState(false);
  const [enrollModalType, setEnrollModalType] =
    useState<CourseEnrollmentTypeEnum>();
  const [enrollModalEntityType, setEnrollModalEntityType] =
    useState<CourseEntityScheduleEnum>();
  const [showDelete, setShowDelete] = useState<boolean>(
    !!(
      (course.status !== CourseStatus.PUBLISHED ||
        isMaterial ||
        isCustomCourse) &&
      !isBranchAdmin &&
      can(Actions.DELETE, Subjects.COURSES)
    ),
  );

  const deleteCourse = useDeleteCourse();
  const handleDeleteCourse = async (courseId: string) => {
    if (isDemoAccount) return;
    try {
      await deleteCourse.delete(courseId);
      toast?.success(t('toast.success'), t('material.archived'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
    navigate(returnUrl || materialByTypeListPath(course.type));
  };

  const menuItems = () => {
    const items = [];

    if (account?.isSystem) {
      items.push(
        ...[
          {
            label: t('generic.accounts.available'),
            icon: 'pi pi-check-circle',
            command: () => setShowMakeAvailableModal(true),
          },
          {
            label: t('generic.accounts.unavailable'),
            icon: 'pi pi-ban',
            command: () => setShowMakeUnavailableModal(true),
          },
        ],
      );

      if (!isPoster) {
        items.push({
          label: t('generic.unenroll'),
          icon: 'pi pi-times',
          command: isDemoAccount
            ? undefined
            : () => {
                setCourseAccountsEnrollModal(true);
                setEnrollModalType(CourseEnrollmentTypeEnum.UNENROLL);
              },
        });
      }

      if (course?.id && onChangeCourseType && !isMaterial) {
        items.push({
          label: selfHosted
            ? t('courses.make.ccHosted')
            : t('courses.make.selfHosted'),
          icon: 'pi pi-question-circle',
          command: isDemoAccount
            ? undefined
            : () =>
                setDialogData({
                  type: DialogType.CONFIRMATION,
                  show: true,
                  header: t('dialog.confirm'),
                  message: t('dialog.change.content.host.type'),
                  onAccept: () => onChangeCourseType(!selfHosted),
                }),
        });
      }

      if (
        account?.isSystem &&
        !retired &&
        can(Actions.UPDATE, Subjects.COURSES) &&
        !showDelete &&
        !isMaterial
      ) {
        items.push({
          label: t('generic.retire'),
          icon: 'pi pi-inbox',
          command: isDemoAccount
            ? undefined
            : () =>
                setDialogData({
                  type: DialogType.CONFIRMATION,
                  show: true,
                  header: t('dialog.confirm'),
                  message: t('dialog.change.content.retire'),
                  onAccept: () => handleRetire(),
                }),
        });
      }
    } else if (!isPoster) {
      items.push(...enrollMenuItems(CourseEnrollmentTypeEnum.UNENROLL));
    }

    if (
      isMaterial &&
      showDelete &&
      (account?.isSystem || isCustomMaterial || isCustomCourse)
    ) {
      items.push({
        label: t('generic.archive'),
        icon: 'pi pi-trash',
        command: isDemoAccount
          ? undefined
          : () => {
              setDialogData({
                type: DialogType.CONFIRMATION,
                show: true,
                header: t('dialog.archive.confirm'),
                message: t('dialog.archive.material'),
                onAccept: async () => handleDeleteCourse(course.id),
              });
            },
      });
    }

    if (
      account?.isSystem &&
      !retired &&
      !isMaterial &&
      !course.account &&
      (user?.role.code === SystemRoles.SUPER_ADMIN ||
        user?.role.code === SystemRoles.DEVELOPER)
    ) {
      items.push({
        label: t('course.migration.menuItem'),
        icon: 'pi pi-arrow-right-arrow-left',
        command: isDemoAccount ? undefined : () => setShowMigrationModal(true),
      });
    }

    return items;
  };

  const handleSubmitAvailable = async (data: CourseAvailabilityFormValues) => {
    if (isDemoAccount) return;
    try {
      await saveAvailableAccounts.add({
        courseId: course?.id,
        accounts: data?.accounts?.map((account) => account.id),
      });
      toast?.success(t('toast.success'), t('courses.accounts.available.added'));
      setShowMakeAvailableModal(false);
      setShowDelete(false);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleSubmitUnavailable = async (
    data: CourseAvailabilityFormValues,
  ) => {
    if (isDemoAccount) return;
    try {
      await saveUnavailableAccounts.remove({
        courseId: course?.id,
        accounts: data?.accounts?.map((account) => account.id),
      });
      toast?.success(
        t('toast.success'),
        t('courses.accounts.unavailable.added'),
      );
      setShowMakeUnavailableModal(false);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const updateCourse = useUpdateCourse();

  const handleRetire = async () => {
    try {
      await updateCourse.update({
        updates: { retired: true, format: course?.format },
        courseId: course?.id,
      });

      toast?.success(
        t('toast.success'),
        isMaterial ? t('material.retire.success') : t('content.retire.success'),
      );

      navigate(returnUrl || materialByTypeListPath(course.type));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const enrollMenuItems = (type: CourseEnrollmentTypeEnum) => {
    const isEnroll = type === CourseEnrollmentTypeEnum.ENROLL;
    const disabled = isSchedulingRestricted;

    const items: MenuItem[] = [
      ...(!isBranchAdmin && !isScopeRestricted
        ? [
            {
              label: isEnroll
                ? account?.name
                : `${t('generic.unenroll')} ${account?.name}`,
              icon: 'pi pi-building',
              disabled,
              command: isDemoAccount
                ? undefined
                : () => {
                    setEnrollModalEntityType(CourseEntityScheduleEnum.ACCOUNT);
                    setEnrollModalType(type);
                    isEnroll ? setEnrollModal(true) : setUnenrollModal(true);
                  },
            },
          ]
        : []),
      ...(can(Actions.READ, Subjects.BRANCHES)
        ? [
            {
              label: isEnroll
                ? t('branches')
                : `${t('generic.unenroll')} ${t('branches')}`,
              icon: 'pi pi-sitemap',
              disabled,
              command: isDemoAccount
                ? undefined
                : () => {
                    setEnrollModalEntityType(CourseEntityScheduleEnum.BRANCH);
                    setEnrollModalType(type);
                    isEnroll ? setEnrollModal(true) : setUnenrollModal(true);
                  },
            },
          ]
        : []),
      ...(can(Actions.UPDATE, Subjects.COURSE_GROUP_SCHEDULES) && !isBranchAdmin
        ? [
            {
              label: isEnroll
                ? t('groups')
                : `${t('generic.unenroll')} ${t('groups')}`,
              icon: 'pi pi-users',
              disabled,
              command: isDemoAccount
                ? undefined
                : () => {
                    setEnrollModalEntityType(CourseEntityScheduleEnum.GROUP);
                    setEnrollModalType(type);
                    isEnroll ? setEnrollModal(true) : setUnenrollModal(true);
                  },
            },
          ]
        : []),
      {
        label: isEnroll ? t('users') : `${t('generic.unenroll')} ${t('users')}`,
        icon: 'pi pi-user',
        disabled,
        command: isDemoAccount
          ? undefined
          : () => {
              setEnrollModalEntityType(CourseEntityScheduleEnum.USER);
              setEnrollModalType(type);
              isEnroll ? setEnrollModal(true) : setUnenrollModal(true);
            },
      },
    ];

    return items.map((item) => ({
      ...item,
      disabled: isSchedulingRestricted,
    }));
  };

  const systemEnrollActionMenu = useRef<any>(null);

  const systemEnrollMenuItems = () => {
    return [
      {
        label: t('accounts'),
        icon: 'pi pi-building',
        command: isDemoAccount
          ? undefined
          : () => {
              setCourseAccountsEnrollModal(true);
              setEnrollModalType(CourseEnrollmentTypeEnum.ENROLL);
            },
      },
    ];
  };

  const handleSystemCourseEnrollment = (
    event?: SyntheticEvent<HTMLButtonElement>,
  ) => {
    systemEnrollActionMenu.current.toggle(event);
  };

  const checkAndTrackHubspotEvent = () => {
    if (!isMaterial) {
      hubspotTrack(
        HubspotProperty.NEW_USER_ENROLLED_TO_A_COURSE,
        HubspotValue.YES,
      );
    }
  };

  const accountsSchedules = useAccountsSchedules();
  const handleSubmitEnrollAccounts = async (data: AccountsEnrollFormValues) => {
    try {
      await accountsSchedules.add({
        accounts: data?.accounts?.map((account) => account.id),
        courses: [course?.id],
        date: data.date,
        autoEnroll: data.autoEnroll,
        type: data.type,
      });

      if (!account?.isSystem) {
        toast?.success(
          t('toast.success'),
          t('courses.accountName.enroll.success', {
            accountName: account?.name,
          }),
        );
        setEnrollModal(false);
      } else {
        toast?.success(
          t('toast.success'),
          t('courses.accounts.enroll.success'),
        );
        setCourseAccountsEnrollModal(false);
      }

      checkAndTrackHubspotEvent();

      if (onTriggerRefetch) {
        onTriggerRefetch();
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const unenrollEntity = useDeleteEnrollment();
  const handleSubmitUnenroll = async ({
    courseId,
    entity,
    value,
  }: DeleteEnrollmentFormValues) => {
    if (isDemoAccount) return;
    try {
      await unenrollEntity.post({
        courseId,
        request: { [entity]: value.map((x) => x.id) },
      });

      toast?.success(
        t('toast.success'),
        t(`courses.${entity}.unenroll.success`),
      );
      setUnenrollModal(false);
      setCourseAccountsEnrollModal(false);

      if (onTriggerRefetch) {
        onTriggerRefetch();
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const branchesSchedules = useBranchesSchedules();
  const handleSubmitEnrollBranches = async (data: EnrollFormValues) => {
    if (isDemoAccount) return;
    try {
      await branchesSchedules.add({
        courses: [course?.id],
        branches: data?.branches?.map((branch) => branch.id) ?? [],
        date: data.date,
        autoEnroll: data.autoEnroll,
        type: data.type,
      });

      toast?.success(t('toast.success'), t('courses.branches.enroll.success'));
      setEnrollModal(false);

      checkAndTrackHubspotEvent();

      if (onTriggerRefetch) {
        onTriggerRefetch();
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const groupsSchedule = useGroupsSchedules();
  const handleSubmitEnrollGroups = async (data: EnrollFormValues) => {
    if (isDemoAccount) return;
    try {
      await groupsSchedule.add({
        courses: [course?.id],
        groups: data?.groups?.map((group) => group.id) ?? [],
        date: data.date,
        autoEnroll: data.autoEnroll,
        type: data.type,
      });

      toast?.success(t('toast.success'), t('courses.group.enroll.success'));
      setEnrollModal(false);

      checkAndTrackHubspotEvent();

      if (onTriggerRefetch) {
        onTriggerRefetch();
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const usersSchedules = useUsersSchedules();
  const handleSubmitEnrollUsers = async (data: EnrollFormValues) => {
    if (isDemoAccount) return;
    try {
      await usersSchedules.add({
        courses: [course?.id],
        users: data?.users?.map((user) => user.id) ?? [],
        date: data.date,
        type: data.type,
      });

      toast?.success(
        t('toast.success'),
        !isResource && moment(data.date).isSame(moment(), 'day')
          ? t('courses.users.enroll.success.notify')
          : t('courses.users.enroll.success'),
      );
      setEnrollModal(false);

      checkAndTrackHubspotEvent();

      if (onTriggerRefetch) {
        onTriggerRefetch();
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleEnrollModalSubmit = async (
    data: EnrollFormValues,
    type?: CourseEnrollmentTypeEnum,
  ) => {
    if (!type || isDemoAccount) return;
    const isEnroll = type === CourseEnrollmentTypeEnum.ENROLL;

    switch (enrollModalEntityType) {
      case CourseEntityScheduleEnum.ACCOUNT: {
        isEnroll
          ? await handleSubmitEnrollAccounts(data as AccountsEnrollFormValues)
          : await handleSubmitUnenroll({
              courseId: course.id,
              entity: CourseEntityEnrollEnum.ACCOUNTS,
              value: data?.accounts as Account[],
            });
        break;
      }
      case CourseEntityScheduleEnum.BRANCH: {
        isEnroll
          ? await handleSubmitEnrollBranches(data)
          : await handleSubmitUnenroll({
              courseId: course.id,
              entity: CourseEntityEnrollEnum.BRANCHES,
              value: data?.branches as Branch[],
            });
        break;
      }
      case CourseEntityScheduleEnum.GROUP: {
        isEnroll
          ? await handleSubmitEnrollGroups(data)
          : await handleSubmitUnenroll({
              courseId: course.id,
              entity: CourseEntityEnrollEnum.GROUPS,
              value: data?.groups as Group[],
            });
        break;
      }
      case CourseEntityScheduleEnum.USER: {
        isEnroll
          ? await handleSubmitEnrollUsers(data)
          : await handleSubmitUnenroll({
              courseId: course.id,
              entity: CourseEntityEnrollEnum.USERS,
              value: data?.users as User[],
            });
        break;
      }
      default:
        break;
    }
  };

  const isEnrollButtonVisible = () => {
    const routes = [
      RedirectPaths[RedirectPathsEnum.COURSES_USERS](course?.id),
      RedirectPaths[RedirectPathsEnum.CUSTOM_MATERIALS_USERS](course?.id),
      RedirectPaths[RedirectPathsEnum.RESOURCES_USERS](course?.id),
    ];

    return isPoster ? false : routes.includes(location.pathname);
  };

  const showCustomizeCourseButton =
    !account?.isSystem && !!course.aiCustomizationSettings?.isEnabled;

  const isCourseCloned = !!course.cloneMetadata?.isClone;
  const isCourseAiCustomized =
    !!course.customizationMetadata?.originalContentId;
  const isCustomizeCourseButtonEnabled =
    !isCourseCloned && !isCourseAiCustomized;

  const actionMenuItems: MenuItem[] = menuItems();
  const areAllActionsRestricted =
    isSchedulingRestricted &&
    actionMenuItems.length > 0 &&
    actionMenuItems.every((item) => item.disabled);

  return (
    <>
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <CourseTranslationStatusBanner courseId={course.id} />
      <AppBreadCrumb
        model={getBreadcrumbs(
          account as Account,
          course,
          language as LanguagesEnum,
          location,
          t,
          returnUrl,
          onRequestLeave,
        )}
      />

      <FlexContainer justify="space-between" align="center" gap={16}>
        <FlexContainer gap={12} justify="flex-start" align="center" flex="1">
          <h2 className="m-0">
            {
              getTranslationByLanguage(
                course?.courseTranslations,
                language as LanguagesEnum,
                course.defaultLanguage,
              )?.name
            }
          </h2>
          {!retired ? (
            <AppChip label={t('generic.active')} type="primary" />
          ) : (
            <AppChip label={t('generic.retired')} type="error" />
          )}
          {course?.account && account?.isSystem && (
            <AppChip
              label={
                isMaterial
                  ? t('generic.custom.content')
                  : t('content.custom.content')
              }
              type="primary"
            />
          )}
        </FlexContainer>
        {showCustomizeCourseButton &&
          (isCustomizeRestricted ? (
            <ViewOnlyRestriction>
              <AppButton size="lg" isDisabled isBeta showBetaTooltip={false}>
                <span className="mr-2">{t('course.customization.button')}</span>
                <WhiteMagicPencilIcon />
              </AppButton>
            </ViewOnlyRestriction>
          ) : (
            <AppButton
              onClick={() =>
                navigate(
                  RedirectPaths[RedirectPathsEnum.COURSES_CUSTOMIZE](course.id),
                )
              }
              size="lg"
              tooltip={
                isCustomizeCourseButtonEnabled
                  ? t('course.customization.tooltip')
                  : isCourseAiCustomized
                  ? t('course.customization.tooltipDisabled')
                  : t('course.customization.tooltipDisabledCloned')
              }
              tooltipOptions={{ position: 'left' }}
              isDisabled={!isCustomizeCourseButtonEnabled}
              isBeta
              showBetaTooltip={isCustomizeCourseButtonEnabled}
            >
              <span className="mr-2">{t('course.customization.button')}</span>
              <WhiteMagicPencilIcon />
            </AppButton>
          ))}
        {!retired && (
          <FlexContainer gap={12} justify="flex-end" flex="0">
            {account?.isSystem && !isPoster ? (
              <>
                <Menu
                  id="overlay_menu_system_enroll"
                  model={systemEnrollMenuItems()}
                  popup
                  ref={systemEnrollActionMenu}
                />
                <AppButton
                  label={t('generic.enroll')}
                  aria-controls="overlay_menu_system_enroll"
                  onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                    handleSystemCourseEnrollment(e);
                  }}
                />
              </>
            ) : (
              <>
                {isEnrollButtonVisible() &&
                  (isSchedulingRestricted ? (
                    <ViewOnlyRestriction>
                      <AppButton label={t('generic.enroll')} isDisabled />
                    </ViewOnlyRestriction>
                  ) : (
                    <AppButton
                      label={t('generic.enroll')}
                      onClick={() => {
                        setEnrollModalEntityType(CourseEntityScheduleEnum.USER);
                        setEnrollModalType(CourseEnrollmentTypeEnum.ENROLL);
                        setEnrollModal(true);
                      }}
                    />
                  ))}
              </>
            )}
            {!!actionMenuItems.length && !isDemoAccount && (
              <ViewOnlyRestrictionWhen restricted={areAllActionsRestricted}>
                <DataTableActions
                  menuItems={actionMenuItems}
                  disabled={areAllActionsRestricted}
                />
              </ViewOnlyRestrictionWhen>
            )}
          </FlexContainer>
        )}

        <CourseAvailabilityModal
          type={CourseAvailabilityTypeEnum.MAKE_AVAILABLE}
          visible={showMakeAvailableModal}
          onSubmit={handleSubmitAvailable}
          onHide={() => setShowMakeAvailableModal(false)}
          course={course}
        />

        <CourseAvailabilityModal
          type={CourseAvailabilityTypeEnum.MAKE_UNAVAILABLE}
          visible={showMakeUnavailableModal}
          onSubmit={handleSubmitUnavailable}
          onHide={() => setShowMakeUnavailableModal(false)}
          course={course}
        />

        <CourseAccountsEnrollModal
          type={enrollModalType}
          course={course}
          visible={showCourseAccountsEnrollModal}
          onSubmit={(data: AccountsEnrollFormValues) =>
            enrollModalType === CourseEnrollmentTypeEnum.ENROLL
              ? handleSubmitEnrollAccounts(data)
              : handleSubmitUnenroll({
                  courseId: course.id,
                  entity: CourseEntityEnrollEnum.ACCOUNTS,
                  value: data.accounts,
                })
          }
          onHide={() => setCourseAccountsEnrollModal(false)}
        />

        <EnrollModal
          isDemoAccount={isDemoAccount}
          courseId={
            enrollModalType === CourseEnrollmentTypeEnum.UNENROLL
              ? course.id
              : undefined
          }
          type={enrollModalType}
          account={account as Account}
          enrollType={enrollModalEntityType}
          visible={
            enrollModalType === CourseEnrollmentTypeEnum.ENROLL
              ? showEnrollModal
              : showUnenrollModal
          }
          onSubmit={(data: EnrollFormValues) => {
            if (showEnrollModal) {
              handleEnrollModalSubmit(data, enrollModalType);
            } else if (showUnenrollModal) {
              handleEnrollModalSubmit(data, enrollModalType);
            }
          }}
          onHide={() => {
            if (showEnrollModal) {
              setEnrollModal(false);
            } else if (showUnenrollModal) {
              setUnenrollModal(false);
            }
          }}
          course={course}
        />

        <CourseMigrationModal
          visible={showMigrationModal}
          onHide={() => setShowMigrationModal(false)}
          courseId={course.id}
          onSuccess={() => {
            onTriggerRefetch?.();
          }}
        />
      </FlexContainer>
    </>
  );
};
